import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";

const DEFAULT_MISSION =
  "Dedicated to introduce reforms to attain peace, prosperity, equality and sustainable development in the ARMM, the PEACI will lead the standards of excellence in science and technology, arts and sports; serve as the catalyst for the economic, cultural, socio-political, and agro-industrial development of the Muslims, Lumads and other indigenous groups, thereby transforming them into good productive members of the society possessing the highest degree of intelligence, honesty, morality, and excellence; and encourage them for the alleviation of poverty and the promotion of employment, and improved quality of life for all.";

const DEFAULT_VISION =
  "The Philippine Engineering and Agro-Industrial College, Inc. aspires to be the modern World-Class institution of highest academic excellence in the Autonomous Region in Muslim Mindanao (ARMM).";

const DEFAULT_OBJECTIVES = [
  "To provide competent, high moral, honest, trustworthy, service-oriented, intelligent, God-fearing and world-class graduates who are skillful and globally competitive in their special field of specialization.",
  "To produce the necessary workforce for the socio-economic, political and agro-industrial development of Mindanao, particularly the ARMM.",
  "To produce globally competitive technicians, educators, researchers and inventors who will serve as instruments for the attainment of mutual understanding, unity and lasting peace and sustainable development of the ARMM.",
];

const DEFAULT_GOALS = [
  "To provide and maintain a progressive educational institution of highest academic standards in strategic areas in Mindanao specially conflict-affected areas of the Autonomous Region in Muslim Mindanao (ARMM), where quality of education is in need.",
  "To offer elementary, secondary, tertiary, graduate and post-graduate courses as well as vocational, scientific and artistic courses in accordance with most updated, relevant, responsive and world-class curriculum at low and reasonable cost. It shall give emphasis on cultural development and preservation and scientific and Islamic researches necessary for the socio-economic, political, and religious development of ARMM.",
  "To provide free, available and accessible quality education for the poorest of the poor, orphans, under-privileged and disadvantaged children. It shall also give more attentive recognition, development and utilization of the extra-academically gifted.",
];

const aboutRef = doc(db, "about", "content");

function AccordionSection({ id, label, openSection, onToggle, children }) {
  const isOpen = openSection === id;
  return (
    <div
      className={`border border-slate-200 rounded-xl overflow-hidden flex flex-col transition-all duration-300 ${
        isOpen ? "flex-1 min-h-0" : "flex-shrink-0"
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors flex-shrink-0"
      >
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isOpen ? "rotate-90" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <div
        className={`border-t border-slate-100 bg-white flex flex-col transition-all duration-300 overflow-hidden ${
          isOpen ? "flex-1 min-h-0" : "max-h-0"
        }`}
      >
        <div className="px-4 pb-4 pt-2 flex flex-col flex-1 min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange }) {
  const handleChange = (index, value) => {
    const updated = items.map((item, i) => (i === index ? value : item));
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...items, ""]);
  };

  const handleRemove = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <div className="flex items-center justify-between flex-shrink-0">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
        <button
          type="button"
          onClick={handleAdd}
          className="text-xs font-medium text-sky-600 border border-sky-300 hover:bg-sky-50 px-3 py-1 rounded-lg transition-colors"
        >
          + Add Item
        </button>
      </div>
      <div className="flex flex-col gap-2 overflow-y-auto flex-1 min-h-0 pr-1">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-shrink-0 mt-2.5 w-5 h-5 rounded-full bg-sky-100 border border-sky-300 flex items-center justify-center text-xs font-bold text-sky-600">
              {i + 1}
            </div>
            <textarea
              rows={3}
              value={item}
              onChange={(e) => handleChange(i, e.target.value)}
              className="flex-1 border border-slate-200 text-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-y bg-slate-50"
            />
            <button
              type="button"
              onClick={() => handleRemove(i)}
              disabled={items.length === 1}
              className="mt-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-500 border border-red-200 text-xs font-medium hover:bg-red-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AboutPeaci() {
  const [mission, setMission] = useState(DEFAULT_MISSION);
  const [vision, setVision] = useState(DEFAULT_VISION);
  const [objectives, setObjectives] = useState(DEFAULT_OBJECTIVES);
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDoc(aboutRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.mission) setMission(data.mission);
          if (data.vision) setVision(data.vision);
          if (data.objectives?.length) setObjectives(data.objectives);
          if (data.goals?.length) setGoals(data.goals);
        }
      } catch (err) {
        console.error("Failed to load About content:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleToggle = (id) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await setDoc(aboutRef, { mission, vision, objectives, goals });
      setSaveStatus("success");
    } catch (err) {
      console.error("Save failed:", err);
      setSaveStatus("error");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 3500);
    }
  };

  const renderLoading = () => (
    <div className="flex justify-center items-center flex-1">
      <div className="flex flex-col items-center gap-2 text-slate-400">
        <div className="h-8 w-8 rounded-full border-4 border-slate-200 border-t-sky-500 animate-spin" />
        <span className="text-sm">Loading content…</span>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="bg-white w-full h-full p-4 rounded-xl shadow-md flex flex-col gap-4 overflow-hidden">
        {/* Header */}
        <div className="border-l-4 border-sky-500 pl-3 flex-shrink-0">
          <h1 className="font-bold text-2xl text-slate-800">About PEACI</h1>
          <p className="text-sm text-slate-400">
            Manage the Mission, Vision, Objectives, and Goals shown on the public About page
          </p>
        </div>

        {loading ? (
          renderLoading()
        ) : (
          <>
            {/* Accordion area */}
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              <AccordionSection
                id="mission"
                label="Mission"
                openSection={openSection}
                onToggle={handleToggle}
              >
                <textarea
                  value={mission}
                  onChange={(e) => setMission(e.target.value)}
                  className="w-full flex-1 min-h-[120px] border border-slate-200 text-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none bg-slate-50"
                />
              </AccordionSection>

              <AccordionSection
                id="vision"
                label="Vision"
                openSection={openSection}
                onToggle={handleToggle}
              >
                <textarea
                  value={vision}
                  onChange={(e) => setVision(e.target.value)}
                  className="w-full flex-1 min-h-[80px] border border-slate-200 text-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none bg-slate-50"
                />
              </AccordionSection>

              <AccordionSection
                id="objectives"
                label="Objectives"
                openSection={openSection}
                onToggle={handleToggle}
              >
                <ListEditor
                  label="Objectives"
                  items={objectives}
                  onChange={setObjectives}
                />
              </AccordionSection>

              <AccordionSection
                id="goals"
                label="Goals"
                openSection={openSection}
                onToggle={handleToggle}
              >
                <ListEditor
                  label="Goals"
                  items={goals}
                  onChange={setGoals}
                />
              </AccordionSection>
            </div>

            {/* Sticky footer */}
            <div className="flex items-center justify-end gap-4 pt-2 border-t border-slate-100 flex-shrink-0">
              {saveStatus === "success" && (
                <span className="text-sm text-green-600 font-medium">
                  Changes saved successfully.
                </span>
              )}
              {saveStatus === "error" && (
                <span className="text-sm text-red-500 font-medium">
                  Save failed. Please try again.
                </span>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 rounded-lg bg-sky-500 text-white font-semibold text-sm hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
}

export default AboutPeaci;
